<template>
  <div class="lg:ml-5 justify-content-around gap-2">
    <!-- Equipment Lists Selection - Only show when at project level -->
    <div
      v-if="!route.params.equipmentListId && equipmentLists.length > 0"
      class="bg-white p-4 rounded-lg shadow mb-4"
    >
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
            <i class="pi pi-list text-white text-sm" />
          </div>
          <div>
            <h3 class="font-semibold text-gray-800">
              Equipment Lists
            </h3>
            <p class="text-xs text-gray-500">
              {{ equipmentLists.length }} equipment lists available
            </p>
          </div>
        </div>
      </div>
            
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div 
          v-for="equipmentList in equipmentLists" 
          :key="equipmentList.id"
          class="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
          @click="selectEquipmentList(equipmentList)"
        >
          <div class="flex justify-between items-start mb-2">
            <h4 class="font-medium text-gray-800">
              {{ equipmentList.eq_title || equipmentList.title || equipmentList.name }}
            </h4>
            <Badge 
              :value="getStatusDisplay(equipmentList.status_id)" 
              :severity="getStatusSeverity(equipmentList.status_id)"
            />
          </div>
          <p class="text-sm text-gray-600 mb-2">
            {{ equipmentList.eq_number || equipmentList.number || equipmentList.equipment_list_number }}
          </p>
          <div class="flex justify-between text-xs text-gray-500">
            <span>Items: {{ equipmentList.total_items || 0 }}</span>
            <span>Progress: {{ equipmentList.scan_percentage || 0 }}%</span>
          </div>
        </div>
      </div>
    </div>
        
    <!-- Loading State - Only show when at project level -->
    <div
      v-else-if="!route.params.equipmentListId && isLoading"
      class="bg-white p-8 rounded-lg shadow mb-4"
    >
      <div class="flex justify-center items-center">
        <i class="pi pi-spin pi-spinner text-2xl text-blue-500 mr-3" />
        <span class="text-gray-600">Loading equipment lists...</span>
      </div>
    </div>
        
    <!-- Error State - Only show when at project level -->
    <div
      v-else-if="!route.params.equipmentListId && error"
      class="bg-white p-8 rounded-lg shadow mb-4"
    >
      <div class="text-center">
        <i class="pi pi-exclamation-triangle text-2xl text-red-500 mb-2" />
        <p class="text-red-600">
          {{ error }}
        </p>
        <Button
          label="Retry"
          class="mt-2"
          @click="loadEquipmentLists"
        />
      </div>
    </div>
        
    <!-- No Equipment Lists - Only show when at project level -->
    <div
      v-else-if="!route.params.equipmentListId"
      class="bg-white p-8 rounded-lg shadow mb-4"
    >
      <div class="text-center">
        <i class="pi pi-info-circle text-2xl text-gray-400 mb-2" />
        <p class="text-gray-600">
          No equipment lists found for this project.
        </p>
      </div>
    </div>

    <!-- Tabs Section - Only show if we have an equipment list selected -->
    <Tabs
      v-if="route.params.equipmentListId"
      :value="route.path.split('/').pop()"
      class="w-full mt-4"
    >
      <TabList class="p-tablist w-full flex border-b justify-between pb-4">
        <!-- Left-aligned tabs: Prepared, Dispatched, Returned, De-prep, Cases -->
        <div class="flex">
          <Tab value="prepared">
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRoute('prepared')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-check-circle" />
                <span>Prepared</span>
              </a>
            </router-link>
          </Tab>
          <Tab value="dispatched">
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRoute('dispatched')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-truck" />
                <span>Dispatched</span>
              </a>
            </router-link>
          </Tab>
          <Tab value="returned">
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRoute('returned')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-undo" />
                <span>Returned</span>
              </a>
            </router-link>
          </Tab>
          <Tab value="deprep">
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRoute('deprep')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-times-circle" />
                <span>De-prep</span>
              </a>
            </router-link>
          </Tab>
          <Tab value="cases">
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRouteWithQuery('cases')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-briefcase" />
                <span>Cases</span>
              </a>
            </router-link>
          </Tab>
        </div>
        <!-- Right-aligned tabs: only visible when they contain data -->
        <div class="flex ml-auto">
          <Tab
            v-if="hasSubhires"
            value="sub-rentals"
          >
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRoute('sub-rentals')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-shopping-cart" />
                <span>Subhires</span>
              </a>
            </router-link>
          </Tab>
          <Tab
            v-if="hasPatches"
            value="patches"
          >
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRoute('patches')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-wrench" />
                <span>Patches</span>
              </a>
            </router-link>
          </Tab>
          <Tab
            v-if="hasCarnet"
            value="carnet"
          >
            <router-link
              v-slot="{ href, navigate }"
              :to="getTabRoute('carnet')"
              custom
            >
              <a
                v-ripple
                :href="href"
                class="flex items-center gap-2 text-inherit"
                @click="navigate"
              >
                <i class="pi pi-file-export" />
                <span>Carnet</span>
              </a>
            </router-link>
          </Tab>
        </div>
      </TabList>
      <TabPanels class="w-full">
        <router-view v-slot="{ Component }">
          <component
            :is="Component"
            :project-contents="projectContents"
          />
        </router-view>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { createProjectAssets } from '@/data/faker';
import { getProjectEquipmentLists, clearOpenCaseId } from '@/services/warehouseEquipmentListService';
import { getWarehouseProjects } from '@/services/warehouseService';
import { getEquipmentListSubHires } from '@/services/subhireEquipmentListService'
import { getDmxFixtures } from '@/services/projectDmxPatchService'

// PrimeVue Components

const route = useRoute();
const router = useRouter();

// Equipment list state
const equipmentLists = ref([]);
const isLoading = ref(true);
const error = ref(null);

// Right-hand tabs: only visible when they have data
const hasSubhires = ref(false);
const hasPatches = ref(false);
const hasCarnet = ref(false);

// Variables for each status
const noneStatusAssets = ref([]);
const preppedStatusAssets = ref([]);
const returnedStatusAssets = ref([]);
const dispatchedStatusAssets = ref([]);
const depreppedStatusAssets = ref([]);

// Load equipment lists for the project
const loadEquipmentLists = async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
        const projectId = route.params.projectId;
        
        // Try the warehouse equipment list API first
        try {
            const data = await getProjectEquipmentLists(projectId);
            equipmentLists.value = data.equipment_lists || [];
            console.log('Loaded equipment lists via equipment list API:', equipmentLists.value);
        } catch (equipmentListError) {
            console.log('Equipment list API failed, trying warehouse projects API...');
            
            // Fallback to warehouse projects API
            const response = await getWarehouseProjects({
                view: 'all_projects' // Get all projects and filter client-side
            });
            
            if (response.success && response.data) {
                const project = response.data.find(p => p.id == projectId);
                equipmentLists.value = project?.equipment_lists || [];
                console.log('Loaded equipment lists via warehouse projects API:', equipmentLists.value);
            } else {
                equipmentLists.value = [];
            }
        }
        
    } catch (err) {
        console.error('Error loading equipment lists:', err);
        error.value = err.response?.data?.error || 'Failed to load equipment lists';
        equipmentLists.value = [];
    } finally {
        isLoading.value = false;
    }
};

// Select an equipment list and navigate to it
const selectEquipmentList = (equipmentList) => {
    const projectId = route.params.projectId;
    const currentTab = route.path.split('/').pop();
    
    // Navigate to the current tab with the equipment list ID in the route
    router.push(`/warehouse-project/${projectId}/${currentTab}/${equipmentList.id}`);
};

// Get status display name
const getStatusDisplay = (statusId) => {
    // This would ideally come from the API response or a status service
    const statusMap = {
        1: 'Draft',
        2: 'Active',
        3: 'Completed',
        4: 'Cancelled'
    };
    return statusMap[statusId] || 'Unknown';
};

// Get status severity for badge styling
const getStatusSeverity = (statusId) => {
    const severityMap = {
        1: 'warning',    // Draft
        2: 'success',    // Active
        3: 'info',       // Completed
        4: 'danger'      // Cancelled
    };
    return severityMap[statusId] || 'secondary';
};

// Get the correct route for tabs, handling equipment list ID
const getTabRoute = (tabName) => {
    const projectId = route.params.projectId;
    const equipmentListId = route.params.equipmentListId;
    
    // If we have an equipment list ID, include it in the route
    if (equipmentListId) {
        return `/warehouse-project/${projectId}/${tabName}/${equipmentListId}`;
    }
    
    // If no equipment list ID, just go to the project level (will show equipment list selection)
    return `/warehouse-project/${projectId}`;
};

// Preserve existing query (e.g., group) when switching tabs
const getTabRouteWithQuery = (tabName) => {
    const base = getTabRoute(tabName);
    const query = new URLSearchParams(route.query).toString();
    return query ? `${base}?${query}` : base;
};

// Computed property to provide the correct project contents based on current route
const projectContents = computed(() => {
    const currentTab = route.path.split('/').pop();
    const equipmentListId = route.params.equipmentListId;
    
    // If we don't have an equipment list ID, we're at the project level (show equipment list selection)
    if (!equipmentListId) {
        return [];
    }
    
    // If we have an equipment list ID, we're in a specific tab
    switch (currentTab) {
        case 'prepared':
            return noneStatusAssets.value;
        case 'dispatched':
            return preppedStatusAssets.value;
        case 'returned':
            return returnedStatusAssets.value;
        case 'deprep':
            return depreppedStatusAssets.value;
        case 'patches':
            return []; // Empty array for patches - will be populated when component is created
        case 'sub-rentals':
            return []; // Empty array for sub-rentals - will be populated when component is created
        case 'carnet':
            return []; // Empty array for carnet - will be populated when component is created
        default:
            return noneStatusAssets.value;
    }
});

// Load optional-tab visibility (subhires, patches, carnet) when equipment list is selected
const loadOptionalTabVisibility = async () => {
    const projectId = route.params.projectId;
    const equipmentListId = route.params.equipmentListId;
    if (!projectId || !equipmentListId) {
        hasSubhires.value = false;
        hasPatches.value = false;
        hasCarnet.value = false;
        return;
    }
    hasSubhires.value = false;
    hasPatches.value = false;
    hasCarnet.value = false;

    try {
        const [subHiresRes, fixturesRes] = await Promise.allSettled([
            getEquipmentListSubHires(projectId, equipmentListId),
            getDmxFixtures(projectId)
        ]);
        if (subHiresRes.status === 'fulfilled') {
            const list = Array.isArray(subHiresRes.value) ? subHiresRes.value : [];
            hasSubhires.value = list.length > 0;
        }
        if (fixturesRes.status === 'fulfilled') {
            const body = fixturesRes.value?.data;
            const list = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
            hasPatches.value = list.length > 0;
        }
        // Carnet: no count API yet; leave hasCarnet false until backend supports it
    } catch (_) {
        // Non-fatal; optional tabs stay hidden
    }
};

// Watch for equipment list ID changes and clear open case when it changes
watch(
    () => route.params.equipmentListId,
    (newEquipmentListId, oldEquipmentListId) => {
        loadOptionalTabVisibility();
        // Only clear if we're actually changing equipment lists (not just initial load)
        if (newEquipmentListId && oldEquipmentListId && newEquipmentListId !== oldEquipmentListId) {
            const projectId = route.params.projectId;
            clearOpenCaseId(projectId, newEquipmentListId);
            console.log('[ViewWarehouseProject] Cleared open case ID due to equipment list change');
        }
        // Also clear when first selecting an equipment list (going from undefined to a value)
        else if (newEquipmentListId && !oldEquipmentListId) {
            const projectId = route.params.projectId;
            clearOpenCaseId(projectId, newEquipmentListId);
            console.log('[ViewWarehouseProject] Cleared open case ID for new equipment list selection');
        }
    }
);

// Load data on mount
onMounted(() => {
    // Only load equipment lists if we're at the project level (no equipment list ID in route)
    if (!route.params.equipmentListId) {
        loadEquipmentLists();
    } else {
        loadOptionalTabVisibility();
    }
    
    // Load fake data for the tab content (this will be replaced by real API calls)
    const assets = createProjectAssets(100);

    // First, sort the assets by group name, with a fallback for undefined groups
    const sortedAssets = assets.sort((a, b) => {
        const groupA = a.group?.name || '';
        const groupB = b.group?.name || '';
        return groupA.localeCompare(groupB);
    });

    // Now filter the assets by their status
    noneStatusAssets.value = sortedAssets.filter(asset => asset.status === 'none');
    preppedStatusAssets.value = sortedAssets.filter(asset => asset.status === 'prepared');
    dispatchedStatusAssets.value = sortedAssets.filter(asset => asset.status === 'dispatched');
    returnedStatusAssets.value = sortedAssets.filter(asset => asset.status === 'returned');
    depreppedStatusAssets.value = sortedAssets.filter(asset => asset.status === 'deprepped');

    // Log the data to verify
    console.log('Assets:', sortedAssets);
    console.log('None Status:', noneStatusAssets.value);
    console.log('Prepped Status:', preppedStatusAssets.value);
    console.log('Dispatched Status:', dispatchedStatusAssets.value);
    console.log('Returned Status:', returnedStatusAssets.value);
    console.log('Deprepped Status:', depreppedStatusAssets.value);
});
</script>

<style scoped>
.p-grid {
    margin-top: 1rem;
}

.tab-button {
    border-radius: 15px 15px 0 0;
    background-color: #f8fafc;
    border: 0px;
    font-family: Arial;
    font-size: 13.33px;
    color: #64788b;
}

/* Remove padding from TabPanels for warehouse project */
:deep(.p-tabpanels) {
    padding: 0 !important;
    margin: 0 !important;
}

:deep(.p-tabpanels > div) {
    padding: 0 !important;
    margin: 0 !important;
}

:deep(.p-tabpanels > div > div) {
    padding: 0 !important;
    margin: 0 !important;
}
</style>
